import {
  Box,
  BoxProps,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spacer,
} from "@chakra-ui/react";
import { FaCheck } from "react-icons/fa6";
import { FormattedMessage } from "react-intl";

import { RasterStyle } from "@nens/lizard/api-v4";

import { getLegendForStyle } from "./legend-colors";

interface GradientLegendProps extends BoxProps {
  uniqueId: string;
  legend: {
    value: number;
    color: string;
  }[];
  isHorizontal?: boolean /* default is vertical */;
  currentRasterStyle: RasterStyle;
  setRasterStyleString: (style: string | null) => void;
}

const OPTIONS = [
  "YlOrRd",
  "Blues",
  "Purples",
  "BluGreRed2",
  "Blues_trans0",
  "BrOrYe",
  "GrRd",
  "RdOrYl",
  "spectral",
];

export function GradientLegend({
  uniqueId,
  legend,
  isHorizontal,
  currentRasterStyle,
  setRasterStyleString,
  ...rest
}: GradientLegendProps) {
  const currentRasterStyleString = currentRasterStyle.getStyle();
  const canRestyle = currentRasterStyle.getCanRestyle();

  const theActualLegend = (
    <Flex
      flexDir={isHorizontal ? "column" : "row"}
      borderRadius={3}
      p="1"
      border="1px solid #fff"
      _hover={
        canRestyle
          ? {
              border: "1px solid #ccc",
            }
          : undefined
      }
    >
      <Box
        w={isHorizontal ? "100%" : "100px"}
        h={isHorizontal ? "20px" : "200px"}
        borderRadius={3}
        overflow="hidden"
        {...rest}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none" /* to make rectangular viewBox fit non-rectangular shape */
        >
          <defs>
            <linearGradient
              id={uniqueId}
              x1="0%"
              y1="0%"
              x2={isHorizontal ? "100%" : "0%"}
              y2={isHorizontal ? "0%" : "100%"}
            >
              {legend.map((step, i) => (
                <stop
                  key={step.value + step.color + i}
                  offset={`${(i / (legend.length - 1)) * 100}%`}
                  stopColor={step.color}
                />
              ))}
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="100" height="100" fill={`url(#${uniqueId})`} />
        </svg>
      </Box>
    </Flex>
  );

  if (!canRestyle) return theActualLegend;

  return (
    <Popover>
      <PopoverTrigger>{theActualLegend}</PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverHeader>
          <Flex gap={4}>
            <FormattedMessage id="legend.select_colormap" defaultMessage="Select colormap" />
            <Spacer />
            <Button onClick={() => setRasterStyleString(null)}>Reset</Button>
          </Flex>
        </PopoverHeader>
        <PopoverBody height="300px" overflowY="scroll">
          {OPTIONS.map((option) => (
            <Flex key={option} mb="1" _hover={{ bgColor: "#f4f4f4", cursor: "pointer" }} p="2">
              <Flex w="25px" justifyContent="center" alignItems="center">
                {currentRasterStyleString === option ? <FaCheck /> : null}
              </Flex>
              <Box
                rounded="sm"
                w="full"
                h="20px"
                style={{
                  background: getLegendForStyle(option),
                }}
                onClick={() => setRasterStyleString(option)}
                title={option}
              ></Box>
            </Flex>
          ))}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
